import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import {
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Box,
  Button,
  Dialog,
  List,
  ListItem,
  ListItemText,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography
} from '@mui/material';
import CustomPagination from 'components/pagination';
import { Http } from 'api/http/http';
import { logout } from 'store/reducers/auth';
import { openStudentInfoInNewTab } from 'utils/studentInfoUtils';

const columns = [
  { id: 'checkBox', label: '', width: 50, align: 'center' },
  { id: 'studentNumber', label: '학번', minWidth: 100, align: 'center' },
  { id: 'studentName', label: '이름', width: 70, align: 'center' },
  { id: 'departmentName', label: '학과', minWidth: 100, align: 'center' },
  { id: 'level', label: '과정', width: 70, align: 'center' },
  { id: 'moduleName', label: '모듈명', width: 80, align: 'center' },
  { id: 'courseNames', label: '교과목명', width: 80, align: 'center' },
  { id: 'completedSemesters', label: '이수학기', minWidth: 100, align: 'center' },
  { id: 'createdAt', label: '신청일자', minWidth: 100, align: 'center' },
  { id: 'action', label: 'action', minWidth: 100, align: 'center' }
];


export default function AdminCertificateAdd() {
  const rowsPerPage = 20;
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subjectList, setSubjectList] = useState([]);
  const [subjectListOpen, setSubjectListOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = () => dispatch(logout());

  const handleRefreshExpired = () => {
    navigate('/studentLogin');
  };

  const http = new Http({ onRefreshExpired: handleRefreshExpired, onLogout: handleLogout });

  const handleChangePage = (event, newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage - 1);
    }
  };

  // 이수증 리스트 불러오기
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await http.getWithToken(`/api/admin/certificates/pending?page=${page}&size=${rowsPerPage}`);
        const sortRows = response.data.result.content
          .map((row) => ({ ...row, certificateStatus: 'WAIT' }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setRows(sortRows);
        setTotalPages(response.data.result.totalPages);
      } catch (error) {
        console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
      }
    };
    fetchData();
  }, [page]);

  // 교과목명 확인 버튼 클릭했을 때, 해당 학생이 신청한 교과목 리스트 모달창 띄우기
  const handleSubjectOpen = (subjectListFromRow) => {
    setSubjectList(subjectListFromRow);
    setSubjectListOpen(true);
  };

  const handleSubjectClose = () => {
    setSubjectListOpen(false);
  };

  // 승인 버튼 눌렀을 때
  const handleApproval = async (row) => {
    try {
      await http.postWithToken('/api/admin/certificates/approve', {
        ids: [row.id]
      });

      alert(`${row.studentNumber}에 대한 승인이 완료되었습니다.`);
      setRows(rows.filter((r) => r.id !== row.id));
    } catch (error) {
      console.log(error);
      alert('승인에 실패하였습니다.', error);
    }
  };

  //'반려' 버튼 눌렀을 때
  const handleReject = async (row) => {
    try {
      await http.postWithToken('/api/admin/certificates/reject', {
        ids: [row.id]
      });

      alert(`${row.studentNumber}에 대한 거절이 완료되었습니다.`);
      setRows(rows.filter((r) => r.id !== row.id));
    } catch (error) {
      alert('거절에 실패하였습니다.', error);
    }
  };

  // 체크박스 변경 핸들러
  const handleCheckBox = (event, index) => {
    const selectedIndex = selected.indexOf(index);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, index);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }

    setSelected(newSelected);
  };

  // 전체선택, 선택해제 핸들러
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n, index) => index);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // 일괄승인, 일괄반려 핸들러
  const batchProcess = async (type) => {
    if (selected.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }

    const payload = {
      ids: selected.map((index) => rows[index].id)
    };

    const url = type === 'approval' ? '/api/admin/certificates/approve' : '/api/admin/certificates/reject';

    try {
      const response = await http.postWithToken(url, payload);
      alert(`선택된 데이터에 대한 일괄${type === 'approval' ? '승인' : '반려'}이 완료되었습니다.`, response.data);
      setRows(rows.filter((_, index) => !selected.includes(index)));
      setSelected([]);
    } catch (error) {
      alert(`일괄${type === 'approval' ? '승인' : '반려'} 중 오류가 발생했습니다`, error);
    }
  };


  // 취소 처리 함수 추가
  const handleCancel = async (row) => {
    if (window.confirm('정말로 취소하시겠습니까?')) {
      try {
        const response = await http.postWithToken(`/api/admin/certificates/${row.id}/cancel`);

        if (response.data.status === 'SUCCESS') {
          alert('취소 처리되었습니다.');
          // 목록 새로고침
          setRows(rows.filter((r) => r.id !== row.id));
        } else {
          alert('취소 처리에 실패했습니다.');
        }
      } catch (error) {
        console.error('취소 처리 중 오류 발생:', error);
        // 백엔드가 내려주는 구체적인 사유(장학증 존재, 이미 취소됨 등)를 그대로 노출한다.
        alert(error.response?.data?.message || '취소 처리 중 오류가 발생했습니다.');
      }
    }
  };


  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.2); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="50vh">
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', marginBottom: '10px', marginRight: '45px' }}>
          <Button variant="contained" color="primary" style={{ marginRight: '5px' }} onClick={() => batchProcess('approval')}>
            선택일괄승인
          </Button>
          <Button variant="contained" color="primary" onClick={() => batchProcess('refusal')}>
            선택일괄반려
          </Button>
        </Box>
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 700, overflowX: 'hidden' }}>
            <Table sx={{ fontSize: '0.8rem', tableLayout: 'fixed', width: '100%' }} stickyHeader aria-label="sticky table">
              <TableHead sx={{ backgroundColor: '#bfbfbf' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < rows.length}
                      checked={rows.length > 0 && selected.length === rows.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell
                      sx={{
                        fontSize: '0.8rem',
                        ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width })
                      }}
                      key={column.id}
                      align={column.align}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell sx={{ textAlign: 'center' }} colSpan={columns.length}>
                      현재 목록이 비어있습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={selected.indexOf(index) !== -1}
                        tabIndex={-1}
                        key={index}
                        selected={selected.indexOf(index) !== -1}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={selected.indexOf(index) !== -1} onChange={(event) => handleCheckBox(event, index)} />
                        </TableCell>
                        {columns.map((column) => {
                          const value = row[column.id];
                          if (column.id === 'action') {
                            return (
                              <TableCell sx={{ fontSize: '0.8rem' }} key={column.id} align={column.align}>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleApproval(row)}
                                    disabled={row.status === 'APPROVED'}
                                    sx={{
                                      minWidth: 'auto',
                                      px: 1,
                                      py: 0.5,
                                      fontSize: '0.7rem'
                                    }}
                                  >
                                    승인
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    onClick={() => handleReject(row)}
                                    disabled={row.status === 'REJECTED'}
                                    sx={{
                                      minWidth: 'auto',
                                      px: 1,
                                      py: 0.5,
                                      fontSize: '0.7rem'
                                    }}
                                  >
                                    반려
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="warning"
                                    onClick={() => handleCancel(row)}
                                    disabled={row.status === 'CANCELLED'}
                                    sx={{
                                      minWidth: 'auto',
                                      px: 1,
                                      py: 0.5,
                                      fontSize: '0.7rem'
                                    }}
                                  >
                                    취소
                                  </Button>
                                </Box>
                              </TableCell>
                            );
                          } else if (column.id === 'createdAt') {
                            let date = new Date(value);
                            let formattedDate =
                              date.getFullYear() +
                              '-' +
                              (date.getMonth() + 1).toString().padStart(2, '0') +
                              '-' +
                              date.getDate().toString().padStart(2, '0') +
                              ' ' +
                              date.getHours().toString().padStart(2, '0') +
                              ':' +
                              date.getMinutes().toString().padStart(2, '0') +
                              ':' +
                              date.getSeconds().toString().padStart(2, '0');

                            return (
                              <TableCell sx={{ fontSize: '0.8rem' }} key={column.id} align={column.align}>
                                {formattedDate}
                              </TableCell>
                            );
                          } else if (column.id === 'level') {
                            let course;
                            if (value === 'LOW') {
                              course = '초급';
                            } else if (value === 'MIDDLE') {
                              course = '중급';
                            } else if (value === 'HIGH') {
                              course = '고급';
                            } else {
                              course = value;
                            }
                            return (
                              <TableCell
                                sx={{
                                  fontSize: '0.8rem',
                                  ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width })
                                }}
                                key={column.id}
                                align={column.align}
                              >
                                {course}
                              </TableCell>
                            );
                          } else if (column.id === 'courseNames') {
                            return (
                              <TableCell sx={{ fontSize: '0.8rem' }} key={column.id} align={column.align}>
                                <Button variant="contained" color="primary" onClick={() => handleSubjectOpen(row.courseNames)}>
                                  확인
                                </Button>
                                <Dialog
                                  open={subjectListOpen}
                                  onClose={handleSubjectClose}
                                  aria-labelledby="alert-dialog-title"
                                  aria-describedby="alert-dialog-description"
                                  BackdropProps={{
                                    style: { backgroundColor: 'rgba(255, 255, 255, 0.4)' }
                                  }}
                                  PaperProps={{
                                    elevation: 3,
                                    sx: {
                                      width: '250px'
                                    }
                                  }}
                                >
                                  <DialogTitle style={{ fontSize: '1.2em', fontWeight: 'bold' }}>{'신청 교과목 리스트'}</DialogTitle>
                                  <DialogContent>
                                    <List style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                                      {Array.isArray(subjectList) ? (
                                        subjectList.map((subject, index) => (
                                          <ListItem key={index} style={{ display: 'list-item', marginBottom: '-10px' }}>
                                            <ListItemText primary={subject} />
                                          </ListItem>
                                        ))
                                      ) : (
                                        <ListItem>
                                          <ListItemText primary="신청한 교과목을 확인할 수 없습니다." />
                                        </ListItem>
                                      )}
                                    </List>
                                  </DialogContent>
                                  <DialogActions>
                                    <Button onClick={handleSubjectClose} color="primary">
                                      닫기
                                    </Button>
                                  </DialogActions>
                                </Dialog>
                              </TableCell>
                            );
                          } else if (column.id === 'completedSemesters') {
                            const isHighSemester = value >= 8;
                            return (
                              <TableCell sx={{ fontSize: '0.8rem' }} key={column.id} align={column.align}>
                                <Tooltip
                                  title={isHighSemester ? "8학기 이상 이수한 학생입니다. 우선 검토가 필요할 수 있습니다." : ""}
                                  placement="top"
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: 0.5
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: isHighSemester ? 'error.main' : 'text.primary',
                                        fontWeight: isHighSemester ? 'bold' : 'normal',
                                        fontSize: '0.8rem'
                                      }}
                                    >
                                      {value}학기
                                    </Typography>
                                    {isHighSemester && (
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          backgroundColor: 'error.main',
                                          animation: 'pulse 2s infinite'
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                            );
                          } else if (column.id === 'studentNumber' || column.id === 'studentName') {
                            return (
                              <TableCell
                                sx={{
                                  fontSize: '0.8rem',
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                key={column.id}
                                align={column.align}
                                onClick={() => openStudentInfoInNewTab(row.studentNumber)}
                              >
                                {column.format && typeof value === 'number' ? column.format(value) : value}
                              </TableCell>
                            );
                          } else {
                            return (
                              <TableCell sx={{ fontSize: '0.8rem' }} key={column.id} align={column.align}>
                                {column.format && typeof value === 'number' ? column.format(value) : value}
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center">
        <CustomPagination count={totalPages} page={page} onChange={handleChangePage} />
      </Box>
    </>
  );
}
